import React, { Component, ChangeEvent } from 'react';
import { connect } from 'react-redux';
import * as actions from '../../actions';
import { Card, CardBody, Col, Row, Form, Button } from 'reactstrap';
import InputGroup from '../Forms/InputGroup';
import { getTranslationKey } from '../../modules/utils/helpers';
import Select from '../Forms/Select';
import { Field, reduxForm, InjectedFormProps } from 'redux-form';
import approve from 'approvejs';
import LanguageSelectSoftware from '../User/Account/LanguageSelectSoftware';
import LanguageSelectApps from '../User/Account/LanguageSelectApps';
import type { Notify } from '../../typings/redux';

interface FieldConfig {
  type: string;
  title?: string;
  placeholder?: string;
  value?: string;
  options?: Array<{ value: string; displayValue: string }>;
  rules: {
    required?: { message: string };
    min?: { min: number; message: string };
    equal?: { value?: string; message?: string };
    [key: string]: any;
  };
}

interface FieldsConfig {
  password: FieldConfig;
  knownFrom: FieldConfig;
  passwordConfirmation?: FieldConfig;
  [key: string]: FieldConfig | undefined;
}

let fields: FieldsConfig = {} as FieldsConfig;

interface Auth {
  whiteLabel: boolean;
  languageApp: number;
  [key: string]: any;
}

interface SetPasswordFormValues {
  password: string;
  knownFrom?: string;
}

interface SetPasswordOwnProps {
  notify: Notify;
  auth: Auth;
  setCurrentUserValue: (data: { key: string; value: string; notSetInUI: boolean }) => Promise<void>;
  changeCurrentUserPassword: (password: string, oldPassword: null, skipConfirm: boolean) => void;
  updateDefaultUserLanguages: (data: { languageApp: number }) => void;
}

type SetPasswordProps = SetPasswordOwnProps &
  InjectedFormProps<SetPasswordFormValues, SetPasswordOwnProps>;

class SetPassword extends Component<SetPasswordProps> {
  constructor(props: SetPasswordProps) {
    super(props);
    this.submit = this.submit.bind(this);
    this.validate = this.validate.bind(this);
    // setup here to get the langauge right
    fields = {
      password: {
        type: 'password',
        title: getTranslationKey('password'),
        placeholder: getTranslationKey('password'),
        rules: {
          required: { message: getTranslationKey('requiredInput') },
          min: {
            min: 6,
            message: getTranslationKey('min6CharsPassword')
          }
        }
      },
      knownFrom: {
        type: 'select',
        value: 'pleaseSelect',
        title: getTranslationKey('knownFrom'),
        options: Object.keys(getTranslationKey('knownFromOptions') || {}).map((key) => ({
          value: key,
          displayValue: getTranslationKey(`knownFromOptions.${key}`)
        })),
        rules: {}
      }
    };
  }

  validate(values: SetPasswordFormValues) {
    const errors: { [key: string]: any } = {};
    if (fields.passwordConfirmation) {
      fields.passwordConfirmation.rules.equal = { value: values.password };
    }

    Object.keys(fields).forEach((fieldKey) => {
      const field = fields[fieldKey];
      if (field) {
        const result = approve.value(values[fieldKey as keyof SetPasswordFormValues], field.rules);
        if (result.errors.length > 0) {
          errors[fieldKey] = result.errors;
        }
      }
    });

    return errors;
  }

  async submit(values: SetPasswordFormValues) {
    const errors = this.validate(values);
    if (Object.keys(errors).length === 0) {
      const { setCurrentUserValue, changeCurrentUserPassword } = this.props;

      if (values.knownFrom) {
        await setCurrentUserValue({
          key: 'knownFrom',
          value: values.knownFrom,
          notSetInUI: true
        });
      }

      changeCurrentUserPassword(values.password, null, false);
    }
  }

  render() {
    const { handleSubmit } = this.props;

    return (
      <Card>
        <CardBody>
          <Form onSubmit={handleSubmit(this.submit)}>
            <h1>{getTranslationKey('setPassword')}</h1>
            <p className="text-muted">{getTranslationKey('onboardingPasswordMessage')}</p>
            {Object.keys(fields).map((fieldKey, index) =>
              fields[fieldKey]!.type === 'select' ? (
                <Field component={Select} key={index} name={fieldKey} field={fields[fieldKey]} />
              ) : (
                <Field
                  component={InputGroup}
                  key={index}
                  name={fieldKey}
                  field={fields[fieldKey]}
                />
              )
            )}
            <Row>
              <Col xs="12">
                <Button
                  color="primary"
                  className="px-4 w-100-p"
                  disabled={this.props.pristine || this.props.invalid || this.props.submitting}
                  type="submit"
                >
                  {getTranslationKey('setPassword')}
                </Button>
              </Col>
            </Row>
            {this.props.auth.whiteLabel && (
              <Row className="m-t-10">
                <Col xs="12">
                  <hr />
                  <LanguageSelectSoftware />
                  <div className="m-t-10" />
                  <LanguageSelectApps
                    selectedValue={this.props.auth.languageApp}
                    label=""
                    onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
                      this.props.updateDefaultUserLanguages({
                        languageApp: parseInt(value, 10)
                      })
                    }
                  />
                </Col>
              </Row>
            )}
          </Form>
        </CardBody>
      </Card>
    );
  }
}

const validate = (values: SetPasswordFormValues) => {
  const errors: { [key: string]: any } = {};
  if (fields.passwordConfirmation) {
    fields.passwordConfirmation.rules.equal = { value: values.password };
  }

  Object.keys(fields).forEach((fieldKey) => {
    const field = fields[fieldKey];
    if (field) {
      const result = approve.value(values[fieldKey as keyof SetPasswordFormValues], field.rules);
      if (result.errors.length > 0) {
        errors[fieldKey] = result.errors;
      }
    }
  });

  return errors;
};

const SetPasswordForm = reduxForm<SetPasswordFormValues, SetPasswordOwnProps>({
  form: 'changePasswordForm',
  validate
})(SetPassword);

const mapStateToProps = (state: any) => ({
  notify: state.notify,
  auth: state.auth
});

export default connect(mapStateToProps, actions)(SetPasswordForm);
